import { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, X, MapPin, Info } from 'lucide-react';
import { motion } from 'framer-motion';

export interface ScMaxCapNotification {
  originalAmount: number;
  cappedAmount: number;
  scMaxCap: number;
  stateName: string;
  stateCode: string;
}

interface ScMaxCapModalProps {
  notification: ScMaxCapNotification | null;
  onClose: () => void;
  refreshOnClose?: boolean;
}

export const ScMaxCapModal = ({ notification, onClose, refreshOnClose = true }: ScMaxCapModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayedNotification, setDisplayedNotification] = useState<ScMaxCapNotification | null>(null);
  const [notificationQueue, setNotificationQueue] = useState<ScMaxCapNotification[]>([]);
  const processedNotificationsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (notification) {
      // Create a unique key for this notification
      const notificationKey = `${notification.originalAmount}-${notification.cappedAmount}-${Date.now()}`;
      
      if (!processedNotificationsRef.current.has(notificationKey)) {
        processedNotificationsRef.current.add(notificationKey);
        setNotificationQueue(prev => {
          if (prev.some(n => 
            n.originalAmount === notification.originalAmount && 
            n.cappedAmount === notification.cappedAmount
          )) {
            return prev;
          }
          return [...prev, notification];
        });
      }
    }
  }, [notification]);

  useEffect(() => {
    if (notificationQueue.length > 0 && !isOpen) {
      const nextNotification = notificationQueue[0];
      setDisplayedNotification(nextNotification);
      setIsOpen(true);
      setNotificationQueue(prev => prev.slice(1));
    }
  }, [notificationQueue, isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      setDisplayedNotification(null);
      onClose();
      // Refresh the page to reload the game with the correct (capped) balance
      // This is needed because we return the original win amount to Alea for their balance calculation,
      // but we only credit the capped amount to the user's actual balance
      if (refreshOnClose) {
        window.location.reload();
      }
    }, 300);
  };

  if (!displayedNotification) return null;

  const formatAmount = (amount: number) => {
    return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const reductionAmount = displayedNotification.originalAmount - displayedNotification.cappedAmount;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-md w-[95vw] p-0 overflow-hidden border-0 bg-transparent shadow-none [&>button]:hidden"
        aria-describedby={undefined}
      >
        <div 
          className="relative rounded-2xl overflow-hidden bg-card"
          style={{
            boxShadow: '0 0 60px hsl(var(--casino-gold) / 0.2), 0 25px 50px hsl(var(--background) / 0.5)',
            border: '1px solid hsl(var(--casino-gold) / 0.3)',
          }}
        >
          {/* Decorative gradient background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div 
              className="absolute top-0 left-1/2 -translate-x-1/2 w-[200%] h-[50%]"
              style={{
                background: 'radial-gradient(ellipse at center, hsl(var(--casino-gold) / 0.15), transparent)',
              }}
            />
          </div>

          {/* Top accent border */}
          <div 
            className="absolute inset-x-0 top-0 h-px"
            style={{
              background: 'linear-gradient(to right, transparent, hsl(var(--casino-gold)), transparent)',
              opacity: 0.4,
            }}
          />

          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-50 w-9 h-9 rounded-full bg-muted/80 hover:bg-muted flex items-center justify-center transition-colors border border-border"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>

          {/* Content */}
          <div className="relative z-10 p-6 sm:p-8">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="text-center space-y-5"
            >
              <DialogHeader className="space-y-4">
                {/* Icon */}
                <motion.div
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="flex justify-center"
                >
                  <motion.div
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    className="p-4 rounded-full bg-amber-500/10 border-2 border-amber-500/30"
                    style={{
                      boxShadow: '0 0 30px hsl(var(--casino-gold) / 0.2)',
                    }}
                  >
                    <Info className="h-10 w-10 text-amber-400" />
                  </motion.div>
                </motion.div>
                
                {/* Title */}
                <motion.div
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.4 }}
                >
                  <DialogTitle className="text-xl sm:text-2xl font-bold text-center text-foreground">
                    Win Amount Adjusted
                  </DialogTitle>
                </motion.div>
                
                {/* State info */}
                <DialogDescription asChild>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.4, duration: 0.5, type: "spring" }}
                    className="flex items-center justify-center gap-2 text-muted-foreground"
                  >
                    <MapPin className="h-4 w-4 text-amber-400" />
                    <span>Your state: <span className="text-foreground font-semibold">{displayedNotification.stateName}</span></span>
                  </motion.div>
                </DialogDescription>
              </DialogHeader>

              {/* Amount Display */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5, duration: 0.5, type: "spring" }}
                className="py-4 space-y-4"
              >
                {/* Original vs Capped */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-xl bg-muted/50 border border-border">
                    <div className="text-xs text-muted-foreground mb-1">Original Win</div>
                    <div className="text-lg font-bold text-foreground/70 line-through">
                      {formatAmount(displayedNotification.originalAmount)} SC
                    </div>
                  </div>
                  <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                    <div className="text-xs text-emerald-400 mb-1">Your Win</div>
                    <div className="text-lg font-bold text-emerald-400">
                      {formatAmount(displayedNotification.cappedAmount)} SC
                    </div>
                  </div>
                </div>

                {/* Explanation */}
                <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Per our Terms and Conditions, Sweeps Rules, Internal Compliance and Regulations, 
                    single wins in <span className="text-foreground font-medium">{displayedNotification.stateName}</span> are 
                    capped at <span className="text-amber-400 font-semibold">{formatAmount(displayedNotification.scMaxCap)} SC</span>.
                  </p>
                </div>

                {/* Reduction info */}
                <div className="text-sm text-muted-foreground">
                  Win reduced by <span className="text-amber-400 font-medium">{formatAmount(reductionAmount)} SC</span>
                </div>
              </motion.div>

              {/* Action Button */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.5 }}
              >
                <Button 
                  onClick={handleClose}
                  className="w-full h-12 font-bold text-base rounded-xl transition-all duration-200 hover:brightness-110 active:scale-[0.98] bg-gradient-to-r from-amber-500 to-amber-600 text-white"
                  style={{
                    boxShadow: '0 4px 20px hsl(var(--casino-gold) / 0.3), 0 2px 8px hsl(var(--background) / 0.3)',
                  }}
                  size="lg"
                >
                  I Understand
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
